import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Shield, Video, Calendar, CheckCircle } from 'lucide-react';

interface ConsentTemplate {
  id: string;
  name: string;
  description?: string;
  content: string;
  form_type: 'telehealth' | 'hipaa' | 'financial' | 'treatment' | 'custom';
  version: number;
  is_default: boolean;
  is_active: boolean;
  is_required_for_booking: boolean;
  is_required_for_telehealth: boolean;
  signature_required: boolean;
  expires_after_days?: number;
}

interface ConsentFormPreviewProps {
  template: ConsentTemplate;
}

const FORM_TYPE_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  telehealth: { label: 'Telehealth Consent', icon: <Video className="w-4 h-4" /> },
  hipaa: { label: 'HIPAA Authorization', icon: <Shield className="w-4 h-4" /> },
  financial: { label: 'Financial Responsibility', icon: <FileText className="w-4 h-4" /> },
  treatment: { label: 'Treatment Consent', icon: <FileText className="w-4 h-4" /> },
  custom: { label: 'Custom Consent', icon: <FileText className="w-4 h-4" /> },
};

const ConsentFormPreview: React.FC<ConsentFormPreviewProps> = ({ template }) => {
  const typeInfo = FORM_TYPE_LABELS[template.form_type] || FORM_TYPE_LABELS.custom;

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {typeInfo.icon}
            <span className="text-sm text-muted-foreground">{typeInfo.label}</span>
          </div>
          <h2 className="text-xl font-semibold">{template.name}</h2>
          {template.description && (
            <p className="text-sm text-muted-foreground">{template.description}</p>
          )}
        </div>
        <div className="flex flex-col gap-1 text-right">
          <Badge variant="outline">Version {template.version}</Badge>
          {template.is_default && <Badge variant="secondary">Default</Badge>}
        </div>
      </div>

      {/* Requirements */}
      <div className="flex flex-wrap gap-2">
        {template.is_required_for_booking && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700">
            <Calendar className="w-3 h-3 mr-1" />
            Required for Booking
          </Badge>
        )}
        {template.is_required_for_telehealth && (
          <Badge variant="outline" className="bg-purple-50 text-purple-700">
            <Video className="w-3 h-3 mr-1" />
            Required for Telehealth
          </Badge>
        )}
        {template.signature_required && (
          <Badge variant="outline" className="bg-green-50 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Signature Required
          </Badge>
        )}
        {template.expires_after_days && (
          <Badge variant="outline">
            Expires after {template.expires_after_days} days
          </Badge>
        )}
      </div>

      <Separator />

      {/* Content Preview */}
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <div
          dangerouslySetInnerHTML={{ __html: template.content }}
          className="[&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mt-4 [&>h2]:mb-2
                     [&>h3]:text-base [&>h3]:font-medium [&>h3]:mt-3 [&>h3]:mb-2
                     [&>p]:text-sm [&>p]:mb-3
                     [&>ul]:text-sm [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-6
                     [&>ol]:text-sm [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6
                     [&>li]:mb-1"
        />
      </div>

      <Separator />

      {/* Signature Preview */}
      {template.signature_required && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Patient signature will be captured here:
          </p>
          <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center">
            <p className="text-muted-foreground text-sm">
              [Signature Capture Area]
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Date: _______________</span>
            <span>IP Address: Captured automatically</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentFormPreview;










